import type {
  CrmContactAction,
  CrmContactActionGroup,
  CrmContactActionTone,
} from "@ease/crm/contact-action-groups";
import { getCrmActionHrefKind, shouldOpenCrmActionHrefInNewTab } from "@ease/crm/safe-action-hrefs";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";

interface CrmContactActionGroupsCardProps {
  groups: CrmContactActionGroup[];
  title?: string;
  description?: string;
  className?: string;
}

export function CrmContactActionGroupsCard({
  groups,
  title = "Profile actions",
  description = "Tenant-configured next actions for this contact. Actions render only for safe internal, HTTPS, phone, SMS, or email destinations.",
  className,
}: CrmContactActionGroupsCardProps) {
  if (groups.length === 0) return null;

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
        </div>
        <span className="shrink-0 rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          {groups.reduce((total, group) => total + group.actions.length, 0)}
        </span>
      </div>
      <div className="mt-4 space-y-4">
        {groups.map((group) => (
          <section key={group.key} className="border-t border-[var(--color-border)] pt-4 first:border-t-0 first:pt-0">
            <div className="flex flex-col gap-1">
              <h4 className="text-sm font-semibold text-[var(--color-foreground)]">{group.label}</h4>
              {group.description ? (
                <p className="text-xs text-[var(--color-muted-foreground)]">{group.description}</p>
              ) : null}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {group.actions.map((action) => (
                <CrmContactActionLink key={`${group.key}:${action.key}`} action={action} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

export function CrmContactActionLink({ action }: { action: CrmContactAction }) {
  const className = cn(
    "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
    getCrmContactActionToneClasses(action.tone),
    action.disabled ? "cursor-not-allowed opacity-60" : "",
  );
  const content = (
    <>
      <span>{action.label}</span>
      <ArrowUpRight className="h-3.5 w-3.5" />
    </>
  );

  if (action.disabled) {
    return (
      <span className={className} title={action.disabledReason ?? undefined} aria-disabled="true">
        {content}
      </span>
    );
  }

  const hrefKind = getCrmActionHrefKind(action.href);
  if (hrefKind === "internal") {
    return (
      <Link to={action.href} className={className} title={action.description ?? undefined}>
        {content}
      </Link>
    );
  }
  if (!hrefKind) return null;

  const openInNewTab = shouldOpenCrmActionHrefInNewTab(action.href);
  return (
    <a
      href={action.href}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noreferrer" : undefined}
      className={className}
      title={action.description ?? undefined}
    >
      {content}
    </a>
  );
}

export function getCrmContactActionToneClasses(tone: CrmContactActionTone) {
  if (tone === "primary") {
    return "border-[var(--color-primary)]/30 bg-[var(--color-primary-light)] text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10";
  }
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)] hover:bg-[var(--color-success)]/15";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)] hover:bg-[var(--color-warning)]/15";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/15";
  }
  return "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]";
}
